import React, { useEffect, useRef, useState } from 'react';
import Hls from 'hls.js';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';
import { getToken } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * HLSPlayer — fetches a short-lived stream token from the backend, then attaches
 * hls.js to the <video> element pointing at /api/cctv/stream/{token}/index.m3u8.
 *
 * Props:
 *   cameraId   - Mongo _id of the camera (from cctv_devices or cameras)
 *   className  - CSS class for the <video>
 *   autoplay   - default true
 *   muted      - default true (required by browsers for autoplay)
 */
const HLSPlayer = ({ cameraId, className = '', autoplay = true, muted = true }) => {
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const [status, setStatus] = useState('connecting'); // connecting | playing | error
  const [errorMsg, setErrorMsg] = useState('');
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const video = videoRef.current;
    if (!video || !cameraId) return undefined;

    const cleanup = () => {
      if (hlsRef.current) {
        try { hlsRef.current.destroy(); } catch { /* noop */ }
        hlsRef.current = null;
      }
    };

    const start = async () => {
      setStatus('connecting');
      setErrorMsg('');
      try {
        const token = getToken();
        const resp = await fetch(
          `${API_URL}/api/cctv/stream/token?camera_id=${encodeURIComponent(cameraId)}`,
          { method: 'POST', headers: { Authorization: `Bearer ${token}` } }
        );
        if (!resp.ok) {
          const txt = await resp.text();
          throw new Error(`Token error (${resp.status}): ${txt.slice(0, 160)}`);
        }
        const { manifest_url } = await resp.json();
        if (cancelled) return;

        const fullUrl = `${API_URL}${manifest_url}`;

        if (Hls.isSupported()) {
          const hls = new Hls({
            lowLatencyMode: true,
            liveDurationInfinity: true,
            maxBufferLength: 6,
            maxLiveSyncPlaybackRate: 1.5,
          });
          hlsRef.current = hls;
          hls.loadSource(fullUrl);
          hls.attachMedia(video);
          hls.on(Hls.Events.MANIFEST_PARSED, () => {
            if (cancelled) return;
            setStatus('playing');
            if (autoplay) video.play().catch(() => { /* user gesture needed */ });
          });
          hls.on(Hls.Events.ERROR, (_evt, data) => {
            if (data.fatal) {
              setStatus('error');
              setErrorMsg(`${data.type}: ${data.details || 'stream failed'}`);
              cleanup();
            }
          });
        } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
          // Safari native HLS
          video.src = fullUrl;
          video.addEventListener('loadedmetadata', () => {
            if (!cancelled) setStatus('playing');
            if (autoplay) video.play().catch(() => { /* noop */ });
          });
          video.addEventListener('error', () => {
            if (!cancelled) {
              setStatus('error');
              setErrorMsg('Native HLS playback failed');
            }
          });
        } else {
          setStatus('error');
          setErrorMsg('HLS playback not supported in this browser');
        }
      } catch (err) {
        if (!cancelled) {
          setStatus('error');
          setErrorMsg(err.message || 'Failed to start stream');
        }
      }
    };

    start();
    return () => {
      cancelled = true;
      cleanup();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cameraId, retryCount]);

  return (
    <div className="relative w-full h-full bg-black" data-testid={`hls-player-${cameraId}`}>
      <video
        ref={videoRef}
        className={className || 'w-full h-full object-contain bg-black'}
        muted={muted}
        playsInline
        controls
        data-testid={`hls-video-${cameraId}`}
      />
      {status === 'connecting' && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/60 text-white">
          <div className="text-center">
            <RefreshCw className="h-8 w-8 mx-auto animate-spin mb-2" />
            <p className="text-sm">Connecting to camera…</p>
            <p className="text-xs text-gray-400 mt-1">Transcoding may take a few seconds</p>
          </div>
        </div>
      )}
      {status === 'error' && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/80 text-gray-200">
          <div className="text-center px-4 max-w-md">
            <AlertCircle className="h-8 w-8 mx-auto mb-2 text-red-400" />
            <p className="text-sm font-medium">Stream unavailable</p>
            {errorMsg && <p className="text-xs text-gray-400 mt-1 break-words">{errorMsg}</p>}
            <Button
              size="sm"
              variant="secondary"
              className="mt-3"
              data-testid={`hls-retry-${cameraId}`}
              onClick={() => setRetryCount((n) => n + 1)}
            >
              Retry
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default HLSPlayer;
